import {
  AutoSave,
  Button,
  Card,
  closeCurrentDialog,
  DataStatus,
  Form,
  openDialog,
  showNotification,
} from 'platform/components';
import {HStack, Show, Text, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {usePostVehicleCustomerMutation} from '@dms/api/customer';
import {
  useGetCustomerInquiryQuery,
  useGetWorkshopInterestQuery,
  useSelectWorkshopInterestVehicleMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  handleApiError,
  ServiceVehicleForm,
  ServiceVehicleFormMode,
  ServiceVehicleFormVariant,
} from '@dms/shared';

import {ApiError, getApiDateString, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {useVehicleDetailsUpdate} from '../hooks/useVehicleDetailsUpdate';
import {useVehicleInformationUpdate} from '../hooks/useVehicleInformationUpdate';
import {VehicleDetailsFormType, VehicleInformationFormType} from '../types';
import {
  transformVehicleDetailsToForm,
  transformVehicleInformationToForm,
} from '../utils/transformCustomerInquiry';
import {CustomerRequests} from './CustomerRequests';
import {SelectedVehicle} from './SelectedVehicle';
import {VehicleDetailsForm} from './VehicleDetailsForm';
import {VehicleInformationForm} from './VehicleInformationForm';

interface CustomerInquiryProps extends RequiredTestIdProps {
  interestId: string;
}

export function CustomerInquiry(props: CustomerInquiryProps) {
  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: props.interestId});

  const {
    data: customerInquiry,
    isLoading,
    isError,
  } = useGetCustomerInquiryQuery({interestId: props.interestId});

  const {onVehicleDetailsUpdate} = useVehicleDetailsUpdate(props.interestId);
  const {onVehicleInformationUpdate} = useVehicleInformationUpdate(props.interestId);

  const [selectVehicle] = useSelectWorkshopInterestVehicleMutation();
  const [postVehicleCustomer] = usePostVehicleCustomerMutation();
  const [selectingVehicleId, setSelectingVehicleId] = useState<string | null>(null);

  const vehicleId = workshopInterest?.vehicleId;
  const isVehicleSelected = isNotNil(vehicleId);

  const handleSelectVehicle = (selectedVehicleId: string) => {
    setSelectingVehicleId(selectedVehicleId);
    selectVehicle({
      id: props.interestId,
      selectVehicleRequestBody: {vehicleId: selectedVehicleId},
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
      })
      .catch(handleApiError)
      .finally(() => {
        setSelectingVehicleId(null);
      });
  };

  const handleCreateServiceVehicle = (formValues: VehicleDetailsFormType) => {
    openDialog(
      <ServiceVehicleForm
        mode={ServiceVehicleFormMode.NEW}
        variant={ServiceVehicleFormVariant.SIMPLE}
        extendedDefaultFormData={{
          vin: formValues.vin,
          registrationPlate: formValues.licensePlate,
          type: formValues.vehicleType,
          make: formValues.make ?? undefined,
          formMake: formValues.make ?? '',
          modelFamily: formValues.modelFamily ?? undefined,
          formModelFamily: formValues.modelFamily ?? '',
          trim: formValues.trim,
          variant: formValues.variant,
          firstRegistrationOn: formValues.firstRegistration,
          manufacturedOnYear: formValues.realProductionYear,
          manufacturedOnMonth: formValues.realProductionMonth,
        }}
        onSuccess={(newVehicleId: string) => {
          const contactPersonId = workshopInterest?.contactPersonId;

          const selectAndAssign = selectVehicle({
            id: props.interestId,
            selectVehicleRequestBody: {vehicleId: newVehicleId},
          })
            .unwrap()
            .then(() => {
              if (isNotNil(contactPersonId)) {
                return postVehicleCustomer({
                  body: {
                    vehicleId: newVehicleId,
                    customerId: contactPersonId,
                    customerType: 'CORVEHICLECUSTOMERTYPE_OPERATOR',
                    eventType: 'vehicle',
                    eventId: newVehicleId,
                    dateFrom: getApiDateString(new Date()),
                  },
                }).unwrap();
              }
            });

          selectAndAssign
            .then(() => {
              showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
              closeCurrentDialog();
            })
            .catch((error: ApiError) => {
              handleApiError(error);
              closeCurrentDialog();
            });
        }}
        onDiscard={closeCurrentDialog}
        data-testid={suffixTestId('createServiceVehicleForm', props)}
      />,
      {
        title: i18n.t('entity.vehicle.labels.createServiceVehicle'),
        withAdditionalFooter: true,
      }
    );
  };

  return (
    <Card
      title={i18n.t('entity.workshopInterest.labels.customerInquiry')}
      isExpandable
      data-testid={suffixTestId('customerInquiry', props)}
    >
      <DataStatus isLoading={isLoading} isError={isError} minHeight={200}>
        <VStack spacing={4}>
          <Show when={!isVehicleSelected}>
            <Form<VehicleDetailsFormType>
              defaultValues={
                customerInquiry
                  ? transformVehicleDetailsToForm(customerInquiry.vehicleDetails)
                  : undefined
              }
              isDisabled={isNotNil(selectingVehicleId)}
            >
              {(control, formApi) => (
                <>
                  <AutoSave<VehicleDetailsFormType> onSave={onVehicleDetailsUpdate} />
                  <VStack spacing={4}>
                    <HStack justify="space-between" align="center">
                      <Text size="base" alternative>
                        {i18n.t('entity.vehicle.labels.vehicleDetails')}
                      </Text>
                      <Button
                        title={i18n.t('entity.vehicle.labels.createServiceVehicle')}
                        leftIcon="maps/directions_car"
                        variant="link"
                        size="small"
                        onClick={() => handleCreateServiceVehicle(formApi.getValues())}
                        data-testid={suffixTestId('createServiceVehicle', props)}
                      />
                    </HStack>
                    <VehicleDetailsForm
                      control={control}
                      formApi={formApi}
                      onVehicleSelect={handleSelectVehicle}
                      selectingVehicleId={selectingVehicleId}
                      data-testid={suffixTestId('vehicleDetails', props)}
                    />
                  </VStack>
                </>
              )}
            </Form>
          </Show>

          <Show when={isVehicleSelected}>
            <VStack spacing={4}>
              <Text size="base" alternative>
                {i18n.t('entity.vehicle.labels.vehicleDetails')}
              </Text>
              <SelectedVehicle
                vehicleId={vehicleId ?? ''}
                interestId={props.interestId}
                data-testid={suffixTestId('selectedVehicle', props)}
              />
            </VStack>
          </Show>

          <Form<VehicleInformationFormType>
            defaultValues={
              customerInquiry
                ? transformVehicleInformationToForm(customerInquiry.vehicleInformation)
                : undefined
            }
          >
            {(control) => (
              <>
                <AutoSave<VehicleInformationFormType> onSave={onVehicleInformationUpdate} />
                <VehicleInformationForm
                  control={control}
                  data-testid={suffixTestId('vehicleInformation', props)}
                />
              </>
            )}
          </Form>

          <CustomerRequests
            interestId={props.interestId}
            customerRequests={customerInquiry?.customerRequests ?? []}
            data-testid={suffixTestId('customerRequests', props)}
          />
        </VStack>
      </DataStatus>
    </Card>
  );
}
